import {grids} from 'quickstart/theme'
import * as Render from 'quickstart/utils/render'
import * as R from 'rambdax'
import {
  Children,
  type ComponentProps,
  type CSSProperties,
  type ElementType,
} from 'react'
import {logger, truish} from 'tizra'
import type {Writable} from 'type-fest'
import S from './styles.module.scss'
import {systemPropNames, useSystem, type UseSystemProps} from './system'

const log = logger('Box')

const TagName = 'div' satisfies ElementType
type TagName = typeof TagName

interface BoxOptions extends Render.Options, Omit<UseSystemProps, 'gap'> {
  col?: boolean | number | string
  gap?: UseSystemProps['gap'] | 'default'
  reversed?: boolean
  row?: boolean
}

const useBox = Render.createHook<TagName, BoxOptions>(
  ({
    children,
    col = false,
    colGap,
    gap,
    reversed = false,
    row = false,
    rowGap,
    ...props
  }) => {
    const nonSystemProps: ComponentProps<TagName> = R.omit(
      systemPropNames as Writable<typeof systemPropNames>,
      props,
    )

    const vars: CSSProperties = {}

    if (col === true) {
      props = {
        flex: '1',
        maxWidth: '100%',
        ...props,
      }
    } else if (col === 'auto') {
      props = {
        flex: '0 0 auto',
        maxWidth: 'none',
        width: 'auto',
        ...props,
      }
    } else if (truish(col)) {
      props = {
        flex: '0 0 var(--col-width)',
        maxWidth: 'var(--col-width)',
        ...props,
      }
      vars['--col-width'] =
        typeof col === 'number'
          ? col > 0 && col <= 1
            ? `${100 * col}%`
            : `${col}px`
          : col
    }

    if (row) {
      props = {
        dir: 'row',
        display: 'flex',
        flex: '1',
        ...props,
      }
    }

    const style: CSSProperties = {
      ...useSystem({
        ...props,
        colGap: colGap === 'default' ? grids.default.gap : colGap,
        gap: gap === 'default' ? grids.default.gap : gap,
        rowGap: rowGap === 'default' ? grids.default.gap : rowGap,
      }),
      ...vars,
    }

    return Render.mergeProps(
      {
        className: S.box,
        style,
        children: reversed ? Children.toArray(children).reverse() : children,
      },
      nonSystemProps as any, // TODO: sigh
    )
  },
)

export type BoxProps<T extends ElementType = TagName> = Render.Props<
  T,
  BoxOptions
>

export const Box = (props: BoxProps) => {
  const boxProps = useBox(props)
  return Render.createElement(props.as || TagName, boxProps)
}
